<script>
  import { onMount } from 'svelte';
  import { authFetch } from '../lib/api.js';
  import { showAlert } from '../lib/stores.js';
  
  let loading = true;
  let error = null;
  let users = [];
  let showCreateModal = false;
  let showEditModal = false;
  let selectedUser = null;
  
  // Form data for new user
  let newUser = {
    username: '',
    password: '',
    email: '',
    name: '',
    role: 'staff'
  };
  
  // Form data for editing user
  let editUser = {
    id: '',
    username: '',
    email: '',
    name: '',
    role: 'staff',
    password: ''
  };
  
  // Load users
  async function fetchUsers() {
    try {
      loading = true;
      error = null;
      
      const response = await authFetch('/users');
      
      if (response.success) {
        users = response.users;
      } else {
        throw new Error(response.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      error = 'Failed to load users. Please try again.';
      showAlert('error', error);
    } finally {
      loading = false;
    }
  }
  
  // Create a new user
  async function createUser() {
    // Validate required fields
    if (!newUser.username || !newUser.password || !newUser.name) {
      showAlert('error', 'Username, password, dan nama wajib diisi');
      return;
    }
    
    try {
      const response = await authFetch('/users', {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      
      if (response.success) {
        showAlert('success', 'User berhasil dibuat');
        
        // Reset form
        newUser = {
          username: '',
          password: '',
          email: '',
          name: '',
          role: 'staff'
        };
        
        showCreateModal = false;
        
        // Refresh users list
        await fetchUsers();
      } else {
        throw new Error(response.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      showAlert('error', err.message || 'Gagal membuat user');
    }
  }
  
  // Open edit modal and populate form
  function openEditModal(user) {
    selectedUser = user;
    editUser = {
      id: user._id,
      username: user.username,
      email: user.email || '',
      name: user.name,
      role: user.role,
      password: ''
    };
    showEditModal = true;
  }
  
  // Update a user
  async function updateUser() {
    // Validate required fields
    if (!editUser.username || !editUser.name) {
      showAlert('error', 'Username dan nama wajib diisi');
      return;
    }
    
    try {
      // Create payload, only include password if it's provided
      const payload = {
        username: editUser.username,
        name: editUser.name,
        email: editUser.email,
        role: editUser.role
      };
      
      if (editUser.password) {
        payload.password = editUser.password;
      }
      
      const response = await authFetch(`/users/${editUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      
      if (response.success) {
        showAlert('success', 'User berhasil diperbarui');
        
        showEditModal = false;
        selectedUser = null;
        
        // Refresh users list
        await fetchUsers();
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      showAlert('error', err.message || 'Gagal memperbarui user');
    }
  }
  
  // Delete a user
  async function deleteUser(id) {
    if (!confirm('Apakah Anda yakin ingin menghapus user ini?')) {
      return;
    }
    
    try {
      const response = await authFetch(`/users/${id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        showAlert('success', 'User berhasil dihapus');
        
        // Refresh users list
        await fetchUsers();
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      showAlert('error', err.message || 'Gagal menghapus user');
    }
  }
  
  // Format date
  function formatDate(dateString) {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }
  
  // Initialize
  onMount(() => {
    fetchUsers();
  });
</script>

<div class="users-container">
  <div class="page-header">
    <h1>Manajemen Pengguna</h1>
    
    <div class="header-actions">
      <button class="add-button" on:click={() => showCreateModal = true}>
        + Tambah User
      </button>
    </div>
  </div>
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>{error}</p>
      <button on:click={fetchUsers}>Coba Lagi</button>
    </div>
  {:else if users.length === 0}
    <div class="empty-state">
      <p>Belum ada pengguna yang terdaftar. Silakan tambahkan pengguna baru.</p>
    </div>
  {:else}
    <div class="users-table-container">
      <table class="users-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Nama</th>
            <th>Email</th>
            <th>Role</th>
            <th>Bergabung</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {#each users as user}
            <tr>
              <td>{user.username}</td>
              <td>{user.name}</td>
              <td>{user.email || '-'}</td>
              <td>
                <span class={`role-badge ${user.role}`}>
                  {user.role === 'admin' ? 'Admin' : 'Staff'}
                </span>
              </td>
              <td>{formatDate(user.createdAt)}</td>
              <td class="actions-cell">
                <button class="edit-button" on:click={() => openEditModal(user)}>
                  Edit
                </button>
                <button class="delete-button" on:click={() => deleteUser(user._id)}>
                  Hapus
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<!-- Create User Modal -->
{#if showCreateModal}
  <div class="modal-backdrop" on:click={() => showCreateModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Tambah Pengguna</h2>
      
      <div class="form-group">
        <label for="username">Username <span class="required">*</span></label>
        <input 
          type="text" 
          id="username" 
          bind:value={newUser.username} 
          placeholder="Masukkan username"
        />
      </div>
      
      <div class="form-group">
        <label for="password">Password <span class="required">*</span></label>
        <input 
          type="password" 
          id="password" 
          bind:value={newUser.password} 
          placeholder="Masukkan password"
        />
      </div>
      
      <div class="form-group">
        <label for="name">Nama <span class="required">*</span></label>
        <input 
          type="text" 
          id="name" 
          bind:value={newUser.name} 
          placeholder="Masukkan nama lengkap"
        />
      </div>
      
      <div class="form-group">
        <label for="email">Email</label>
        <input 
          type="email" 
          id="email" 
          bind:value={newUser.email} 
          placeholder="Masukkan email (opsional)"
        />
      </div>
      
      <div class="form-group">
        <label for="role">Role</label>
        <select id="role" bind:value={newUser.role}>
          <option value="admin">Admin</option>
          <option value="staff">Staff</option>
        </select>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showCreateModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={createUser}>
          Simpan
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Edit User Modal -->
{#if showEditModal && selectedUser}
  <div class="modal-backdrop" on:click={() => showEditModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Edit Pengguna</h2>
      
      <div class="form-group">
        <label for="edit-username">Username <span class="required">*</span></label>
        <input 
          type="text" 
          id="edit-username" 
          bind:value={editUser.username} 
          placeholder="Masukkan username"
        />
      </div>
      
      <div class="form-group">
        <label for="edit-password">Password</label>
        <input 
          type="password" 
          id="edit-password" 
          bind:value={editUser.password} 
          placeholder="Masukkan password baru (kosongkan jika tidak ingin mengubah)"
        />
        <p class="help-text">Kosongkan jika tidak ingin mengubah password</p>
      </div>
      
      <div class="form-group">
        <label for="edit-name">Nama <span class="required">*</span></label>
        <input 
          type="text" 
          id="edit-name" 
          bind:value={editUser.name} 
          placeholder="Masukkan nama lengkap"
        />
      </div>
      
      <div class="form-group">
        <label for="edit-email">Email</label>
        <input 
          type="email" 
          id="edit-email" 
          bind:value={editUser.email} 
          placeholder="Masukkan email (opsional)"
        />
      </div>
      
      <div class="form-group">
        <label for="edit-role">Role</label>
        <select id="edit-role" bind:value={editUser.role}>
          <option value="admin">Admin</option>
          <option value="staff">Staff</option>
        </select>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showEditModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={updateUser}>
          Perbarui
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .users-container {
    padding: 0;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .page-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .add-button {
    padding: 0.5rem 1rem;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .add-button:hover {
    background-color: #2563eb;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 0;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.25rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    background-color: #fee2e2;
    border-radius: 0.5rem;
    color: #b91c1c;
    text-align: center;
  }
  
  .error-container button {
    margin-top: 1rem;
    padding: 0.5rem 1.25rem;
    background-color: #dc2626;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .error-container button:hover {
    background-color: #b91c1c;
  }
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    color: #6b7280;
  }
  
  .users-table-container {
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .users-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .users-table thead {
    background-color: #f8fafc;
  }
  
  .users-table th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .users-table td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: #1f2937;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .users-table tr:last-child td {
    border-bottom: none;
  }
  
  .actions-cell {
    display: flex;
    gap: 0.5rem;
  }
  
  .role-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .role-badge.admin {
    background-color: #dbeafe;
    color: #1d4ed8;
  }
  
  .role-badge.staff {
    background-color: #dcfce7;
    color: #16a34a;
  }
  
  .edit-button, .delete-button {
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
  }
  
  .edit-button {
    background-color: #3b82f6;
    color: white;
  }
  
  .edit-button:hover {
    background-color: #2563eb;
  }
  
  .delete-button {
    background-color: #ef4444;
    color: white;
  }
  
  .delete-button:hover {
    background-color: #dc2626;
  }
  
  /* Modals */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 0.5rem;
    padding: 1.5rem;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  
  .modal-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 1.5rem 0;
  }
  
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.5rem;
  }
  
  .required {
    color: #ef4444;
  }
  
  .form-group input, .form-group select {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .help-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
  
  .save-button, .cancel-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .save-button {
    background-color: #3b82f6;
    color: white;
    border: none;
  }
  
  .save-button:hover {
    background-color: #2563eb;
  }
  
  .cancel-button {
    background-color: #f3f4f6;
    color: #4b5563;
    border: 1px solid #d1d5db;
  }
  
  .cancel-button:hover {
    background-color: #e5e7eb;
  }
  
  @media (max-width: 768px) {
    .users-table-container {
      overflow-x: auto;
    }
    
    .users-table {
      min-width: 650px;
    }
  }
</style>